/**
 * Plan Table — subscription plans on the shared DataTable.
 */

import * as React from 'react';
import {
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type SortingState,
} from '@tanstack/react-table';
import { Edit, MoreHorizontal, Trash2, CreditCard } from 'lucide-react';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui/empty';
import { DataTable, DataTableColumnHeader } from '@/components/ui/data-table';
import type { SubscriptionPlan } from '@repo/types';

interface PlanTableProps {
  data: SubscriptionPlan[];
  canManage?: boolean;
  onEditClick?: (plan: SubscriptionPlan) => void;
  onDeleteClick?: (plan: SubscriptionPlan) => void;
  className?: string;
}

/** Format a price string/number with its ISO currency code. */
function formatPrice(price: string, currency: string): string {
  const value = Number(price);
  if (Number.isNaN(value)) return `${price} ${currency}`;
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      maximumFractionDigits: 2,
    }).format(value);
  } catch {
    return `${value.toFixed(2)} ${currency}`;
  }
}

export function PlanTable({
  data,
  canManage = false,
  onEditClick,
  onDeleteClick,
  className,
}: PlanTableProps) {
  const [sorting, setSorting] = React.useState<SortingState>([]);

  const columns: ColumnDef<SubscriptionPlan>[] = [
    {
      accessorKey: 'name',
      header: ({ column }) => <DataTableColumnHeader column={column} label="Name" />,
      cell: ({ row }) => (
        <div className="min-w-0">
          <div className="truncate font-medium">{row.original.name}</div>
          <div className="truncate font-mono text-xs text-muted-foreground">{row.original.slug}</div>
        </div>
      ),
    },
    {
      accessorKey: 'tier',
      header: ({ column }) => <DataTableColumnHeader column={column} label="Tier" />,
      cell: ({ row }) => <span className="text-sm">{row.original.tier}</span>,
    },
    {
      accessorKey: 'price',
      header: ({ column }) => <DataTableColumnHeader column={column} label="Price" />,
      // Sort numerically even though `price` surfaces as a string from pg.
      sortingFn: (a, b) => Number(a.original.price) - Number(b.original.price),
      cell: ({ row }) => (
        <span className="text-sm font-medium tabular-nums whitespace-nowrap">
          {formatPrice(row.original.price, row.original.currency)}
        </span>
      ),
    },
    {
      id: 'duration',
      header: 'Duration',
      cell: ({ row }) => {
        const plan = row.original;
        const unit = plan.duration_value === 1 ? plan.duration_unit : `${plan.duration_unit}s`;
        return (
          <span className="whitespace-nowrap text-sm">
            {plan.duration_value} {unit}
          </span>
        );
      },
    },
    {
      id: 'features',
      header: 'Entitlements',
      cell: ({ row }) => {
        const plan = row.original;
        return (
          <div className="flex flex-wrap gap-1">
            <Badge variant="secondary" className="font-normal">
              {plan.daily_new_jobs_visible}/day visible
            </Badge>
            {plan.job_recommendation_enabled && (
              <Badge variant="secondary" className="font-normal">
                {plan.daily_jobs_recommended} recs
              </Badge>
            )}
            {plan.auto_apply_enabled && (
              <Badge variant="secondary" className="font-normal">
                {plan.daily_jobs_applied} auto-apply
              </Badge>
            )}
            {plan.is_internal && <Badge variant="outline">Internal</Badge>}
          </div>
        );
      },
    },
    {
      accessorKey: 'active',
      header: 'Status',
      cell: ({ row }) =>
        row.original.active ? (
          <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-500/20 bg-emerald-500/10 px-2 py-0.5 text-xs font-medium text-emerald-700 dark:text-emerald-400">
            <span className="size-1.5 rounded-full bg-emerald-500" />
            Active
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
            <span className="size-1.5 rounded-full bg-muted-foreground/50" />
            Inactive
          </span>
        ),
    },
  ];

  if (canManage) {
    columns.push({
      id: 'actions',
      header: () => <span className="sr-only">Actions</span>,
      cell: ({ row }) => {
        const plan = row.original;
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="size-8">
                <span className="sr-only">Open menu</span>
                <MoreHorizontal className="size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-40">
              <DropdownMenuLabel>Actions</DropdownMenuLabel>
              <DropdownMenuItem onClick={() => onEditClick?.(plan)}>
                <Edit className="size-4" />
                Edit plan
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="text-destructive focus:text-destructive"
                onClick={() => onDeleteClick?.(plan)}
              >
                <Trash2 className="size-4" />
                Delete plan
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    });
  }

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    state: { sorting },
  });

  return (
    <DataTable
      table={table}
      className={className}
      emptyState={
        <Empty>
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <CreditCard className="size-6 text-muted-foreground" />
            </EmptyMedia>
            <EmptyTitle>No plans found</EmptyTitle>
            <EmptyDescription>Create a subscription plan to get started.</EmptyDescription>
          </EmptyHeader>
        </Empty>
      }
    />
  );
}
